import { engineComponent, StringUtils, ArrayUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import {
    Configuration,
    RouletteBetConfigurationAttributes,
    RouletteGridConfigurationAttributes,
    RouletteGridEdgeBetsConfigurationAttributes,
    RouletteSingleBetLimitConfigurationAttributes,
} from "../Components/Configuration";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { ImageTextElement } from "../Components/Elements/Texts/ImageTextElement";
import { ImageElement } from "../Components/Elements/Graphics/ImageElement";
import { Bet } from "./Bet";
import { RouletteBetConfiguration } from "../CustomComponents/RouletteBetConfiguration";
import { RouletteGridConfiguration } from "../CustomComponents/RouletteGridConfiguration";
import { GridBets } from "./GridBets";
import { GridElement } from "./GridElement";
import { BetElement } from "./BetElement";
import { RouletteActions } from "../Channels/Common/Actions/RouletteActions";
import { BaseElement } from "../Components/Elements/Graphics/BaseElement";
import { GridPossibleWins } from "./GridPossibleWins";
import { StyleManager } from "../Components/Managers/StyleManager";
import { StatisticsManager } from "../Managers/StatisticsManager";

@engineComponent(ComponentTypes.DisplayObject)
export class Grid extends ContainerElement {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    protected _gridElements: { [id: string]: GridElement };

    protected _gridBets: GridBets;

    protected _gridPossibleWins: GridPossibleWins;

    protected _gridSchemaBets: GridBets;

    protected _gridSchemaPossibleWins: GridPossibleWins;

    protected _gridWinPoint: ContainerElement;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this._gridElements = {};
    }

    //******************************************************************************************************************
    //  CREATE
    //******************************************************************************************************************

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    public create(node: Element): void {
        super.create(node);

        if (node.hasAttribute(GridAttributes.CONFIGS)) {
            let configs: Array<string> = node.getAttribute(GridAttributes.CONFIGS).split(";");
            for (let i: number = 0; i < configs.length; i++) {
                let config: Element = Configuration.Configurations[configs[i]];
                if (config != null) {
                    if (configs[i] == "BetTypesConfiguration") {
                        this.configurateBets(config);
                    } else {
                        this.configurate(config);
                    }
                }
            }
        }

        if (this.childs["GridBets"]) {
            this._gridBets = this.childs["GridBets"];
        }

        if (this.childs["GridSchemaBets"]) {
            this._gridSchemaBets = this.childs["GridSchemaBets"];
        }

        if (this.childs["GridWinPoint"]) {
            this._gridWinPoint = <ContainerElement>this.childs["GridWinPoint"];
        }

        if (this.childs["GridPossibleWins"]) {
            this._gridPossibleWins = <GridPossibleWins>this.childs["GridPossibleWins"];
        }

        if (this.childs["GridSchemaPossibleWins"]) {
            this._gridSchemaPossibleWins = <GridPossibleWins>this.childs["GridSchemaPossibleWins"];
        }
    }

    public initialize(): void {
        this.createNumbers();

        if (this._gridBets != null) {
            this._gridBets.createGridBets(this, this._gridElements, this.childs["GridBetsValue"]);

            if (this._gridPossibleWins != null) {
                this._gridPossibleWins.initialize(this._gridBets.getBetElements());
            }
        }

        if (this._gridSchemaBets != null) {
            this._gridSchemaBets.createGridBets(this, this._gridElements, null, false);

            if (this._gridSchemaPossibleWins != null) {
                this._gridSchemaPossibleWins.initialize(this._gridSchemaBets.getBetElements());
            }
        }
    }

    private createNumbers(): void {
        let container = <ContainerElement>this.childs["GridNumbers"];
        let elem: GridElement;
        let x = 0;
        let y = 0;

        //create zero
        elem = this.createGridElement("N_0", "SS_zeroNumber", "SS_portraitZeroNumber");
        this.setNumberBitmapText(elem, "0");
        this._gridElements["N_0"] = elem;
        container.addChild(elem);
        StatisticsManager.addHitMapElement("N_" + 0, elem);

        //create numbers
        for (let i: number = 0; i < 12; i++) {
            for (let j: number = 0; j < 3; j++) {
                let currentNumber = i * 3 + j + 1;

                x = RouletteGridConfiguration.zeroWidth + i * RouletteGridConfiguration.numberWidth;
                y = (2 - j) * RouletteGridConfiguration.numberHeight;

                elem = this.createGridElement(
                    "N_" + currentNumber,
                    "SS_number",
                    "SS_portraitNumber",
                    currentNumber > 1,
                );
                elem.x = x;
                elem.y = y;

                this.setNumberBitmapText(elem, "" + currentNumber);
                this._gridElements["N_" + currentNumber] = elem;
                container.addChild(elem);
                StatisticsManager.addHitMapElement("N_" + currentNumber, elem);
            }
        }

        //create column
        x = RouletteGridConfiguration.gridWidth - RouletteGridConfiguration.columnWidth;
        for (let i: number = 0; i < 3; i++) {
            y = (2 - i) * RouletteGridConfiguration.columnHeight;

            elem = this.createGridElement("C_" + i, "SS_column", "SS_portraitColumn", i > 0);
            elem.x = x;
            elem.y = y;

            this._gridElements["C_" + i] = elem;
            container.addChild(elem);
            StatisticsManager.addHitMapElement("C_" + i, elem);
        }

        //create dozen
        y = RouletteGridConfiguration.zeroHeight;
        for (let i: number = 0; i < 3; i++) {
            x = i * RouletteGridConfiguration.dozenWidth + RouletteGridConfiguration.zeroWidth;

            elem = this.createGridElement("D_" + i, "SS_dozen", "SS_portraitDozen", i > 0);
            elem.x = x;
            elem.y = y;

            if (i > 0) {
                this.setBitmapTextureByIndex(elem, "GFX_grid_dozen", i);
            }
            this._gridElements["D_" + i] = elem;
            container.addChild(elem);
            StatisticsManager.addHitMapElement("D_" + i, elem);
        }

        //create paired
        y = RouletteGridConfiguration.zeroHeight + RouletteGridConfiguration.dozenHeight;
        for (let i: number = 0; i < 6; i++) {
            let index = i < 3 ? 0 : 1;
            let types = [
                RouletteActions.ActionBetLowOrHigh,
                RouletteActions.ActionBetEvenOrOdd,
                RouletteActions.ActionBetRedOrBlack,
                RouletteActions.ActionBetRedOrBlack,
                RouletteActions.ActionBetEvenOrOdd,
                RouletteActions.ActionBetLowOrHigh,
            ];

            x = i * RouletteGridConfiguration.pairedWidth + RouletteGridConfiguration.zeroWidth;

            elem = this.createGridElement(types[i] + index, "SS_paired", "SS_portraitPaired", i > 0);
            elem.x = x;
            elem.y = y;

            if (i > 0) {
                this.setBitmapTextureByIndex(elem, "GFX_grid_paired", i);
            }
            this._gridElements[types[i] + index] = elem;
            container.addChild(elem);
            StatisticsManager.addHitMapElement(types[i] + index, elem);
        }
    }

    //******************************************************************************************************************
    //  CONFIGURATION
    //******************************************************************************************************************

    /**
     * Get attributes from configuration and set up supported properties.
     * @method configurate
     * @param node is Element from xml
     * @return void
     */
    public configurateBets(node: Element): void {
        if (node.hasAttribute(RouletteBetConfigurationAttributes.SINGLE)) {
            RouletteBetConfiguration.Single = ArrayUtils.numberArray(
                node.getAttribute(RouletteBetConfigurationAttributes.SINGLE),
            );
        }

        if (node.hasAttribute(RouletteBetConfigurationAttributes.SPLIT_VERTICAL)) {
            RouletteBetConfiguration.SplitVertical = ArrayUtils.numberArray(
                node.getAttribute(RouletteBetConfigurationAttributes.SPLIT_VERTICAL),
            );
        }

        if (node.hasAttribute(RouletteBetConfigurationAttributes.SPLIT_HORIZONTAL)) {
            RouletteBetConfiguration.SplitHorizontal = ArrayUtils.numberArray(
                node.getAttribute(RouletteBetConfigurationAttributes.SPLIT_HORIZONTAL),
            );
        }

        if (node.hasAttribute(RouletteBetConfigurationAttributes.STREET)) {
            RouletteBetConfiguration.Street = ArrayUtils.numberArray(
                node.getAttribute(RouletteBetConfigurationAttributes.STREET),
            );
        }

        if (node.hasAttribute(RouletteBetConfigurationAttributes.CORNER)) {
            RouletteBetConfiguration.Corner = ArrayUtils.numberArray(
                node.getAttribute(RouletteBetConfigurationAttributes.CORNER),
            );
        }

        if (node.hasAttribute(RouletteBetConfigurationAttributes.DOUBLE_STREET)) {
            RouletteBetConfiguration.DoubleStreet = ArrayUtils.numberArray(
                node.getAttribute(RouletteBetConfigurationAttributes.DOUBLE_STREET),
            );
        }

        if (node.hasAttribute(RouletteBetConfigurationAttributes.TRIO)) {
            RouletteBetConfiguration.Trio = ArrayUtils.numberArray(
                node.getAttribute(RouletteBetConfigurationAttributes.TRIO),
            );
        }

        if (node.hasAttribute(RouletteBetConfigurationAttributes.FIRST_FOUR)) {
            RouletteBetConfiguration.FirstFour = ArrayUtils.numberArray(
                node.getAttribute(RouletteBetConfigurationAttributes.FIRST_FOUR),
            );
        }

        if (node.hasAttribute(RouletteBetConfigurationAttributes.BASKET)) {
            RouletteBetConfiguration.Basket = ArrayUtils.numberArray(
                node.getAttribute(RouletteBetConfigurationAttributes.BASKET),
            );
        }

        if (node.hasAttribute(RouletteBetConfigurationAttributes.LOW_OR_HIGH)) {
            RouletteBetConfiguration.LowOrHigh = ArrayUtils.numberArray(
                node.getAttribute(RouletteBetConfigurationAttributes.LOW_OR_HIGH),
            );
        }

        if (node.hasAttribute(RouletteBetConfigurationAttributes.RED_OR_BLACK)) {
            RouletteBetConfiguration.RedOrBlack = ArrayUtils.numberArray(
                node.getAttribute(RouletteBetConfigurationAttributes.RED_OR_BLACK),
            );
        }

        if (node.hasAttribute(RouletteBetConfigurationAttributes.EVEN_OR_ODD)) {
            RouletteBetConfiguration.EvenOrOdd = ArrayUtils.numberArray(
                node.getAttribute(RouletteBetConfigurationAttributes.EVEN_OR_ODD),
            );
        }

        if (node.hasAttribute(RouletteBetConfigurationAttributes.DOZEN)) {
            RouletteBetConfiguration.Dozen = ArrayUtils.numberArray(
                node.getAttribute(RouletteBetConfigurationAttributes.DOZEN),
            );
        }

        if (node.hasAttribute(RouletteBetConfigurationAttributes.COLUMN)) {
            RouletteBetConfiguration.Column = ArrayUtils.numberArray(
                node.getAttribute(RouletteBetConfigurationAttributes.COLUMN),
            );
        }
    }

    /**
     * Get attributes from configuration and set up supported properties.
     * @method configurate
     * @param node is Element from xml
     * @return void
     */
    private configurate(node: Element): void {
        if (node.hasAttribute(RouletteGridConfigurationAttributes.GRID_WIDTH)) {
            RouletteGridConfiguration.gridWidth = parseInt(
                node.getAttribute(RouletteGridConfigurationAttributes.GRID_WIDTH),
            );
        }

        if (node.hasAttribute(RouletteGridConfigurationAttributes.GRID_HEIGHT)) {
            RouletteGridConfiguration.gridHeight = parseInt(
                node.getAttribute(RouletteGridConfigurationAttributes.GRID_HEIGHT),
            );
        }

        if (node.hasAttribute(RouletteGridConfigurationAttributes.ZERO_WIDTH)) {
            RouletteGridConfiguration.zeroWidth = parseInt(
                node.getAttribute(RouletteGridConfigurationAttributes.ZERO_WIDTH),
            );
        }

        if (node.hasAttribute(RouletteGridConfigurationAttributes.COLUMN_WIDTH)) {
            RouletteGridConfiguration.columnWidth = parseInt(
                node.getAttribute(RouletteGridConfigurationAttributes.COLUMN_WIDTH),
            );
        }

        if (node.hasAttribute(RouletteGridConfigurationAttributes.DOZEN_HEIGHT)) {
            RouletteGridConfiguration.dozenHeight = parseInt(
                node.getAttribute(RouletteGridConfigurationAttributes.DOZEN_HEIGHT),
            );
        }

        if (node.hasAttribute(RouletteGridConfigurationAttributes.PAIRED_HEIGHT)) {
            RouletteGridConfiguration.pairedHeight = parseInt(
                node.getAttribute(RouletteGridConfigurationAttributes.PAIRED_HEIGHT),
            );
        }

        if (node.hasAttribute(RouletteGridEdgeBetsConfigurationAttributes.WIDTH)) {
            RouletteGridConfiguration.EdgeButtonWidth = parseInt(
                node.getAttribute(RouletteGridEdgeBetsConfigurationAttributes.WIDTH),
            );
        }

        if (node.hasAttribute(RouletteGridEdgeBetsConfigurationAttributes.HEIGHT)) {
            RouletteGridConfiguration.EdgeButtonHeight = parseInt(
                node.getAttribute(RouletteGridEdgeBetsConfigurationAttributes.HEIGHT),
            );
        }

        if (node.hasAttribute(RouletteGridEdgeBetsConfigurationAttributes.CHIP_OFFSET_Y)) {
            RouletteGridConfiguration.ChipOffsetY = parseInt(
                node.getAttribute(RouletteGridEdgeBetsConfigurationAttributes.CHIP_OFFSET_Y),
            );
        }

        this.calculateRemainingSizes();
    }

    private calculateRemainingSizes(): void {
        let innerGridWidth =
            RouletteGridConfiguration.gridWidth -
            RouletteGridConfiguration.zeroWidth -
            RouletteGridConfiguration.columnWidth;
        let innerGridHeight =
            RouletteGridConfiguration.gridHeight -
            RouletteGridConfiguration.pairedHeight -
            RouletteGridConfiguration.dozenHeight;

        RouletteGridConfiguration.numberWidth = innerGridWidth / 12;
        // RouletteGridConfiguration.numberWidth = Math.round(innerGridWidth / 12);
        RouletteGridConfiguration.numberHeight = Math.round(innerGridHeight / 3);
        RouletteGridConfiguration.zeroHeight = innerGridHeight;
        RouletteGridConfiguration.columnHeight = RouletteGridConfiguration.numberHeight;
        RouletteGridConfiguration.dozenWidth = RouletteGridConfiguration.numberWidth * 4;
        RouletteGridConfiguration.pairedWidth = RouletteGridConfiguration.numberWidth * 2;
    }

    //******************************************************************************************************************
    //  PUBLIC HANDLER
    //******************************************************************************************************************

    public placeGridBet(roulletBet: Bet): void {
        if (this._gridBets != null) {
            this._gridBets.placeBet(roulletBet);
        }

        if (this._gridPossibleWins != null) {
            this._gridPossibleWins.updatePossibleWins();
        }
    }

    public hideBets(winNumber: number): void {
        if (this._gridPossibleWins != null) {
            this._gridPossibleWins.hidePossibleWins(winNumber);
        }
    }

    public resetBets(): void {
        if (this._gridBets != null) {
            this._gridBets.resetBets();
        }

        if (this._gridPossibleWins != null) {
            this._gridPossibleWins.resetPossibleWins();
        }
    }

    public getGridElements(): { [id: string]: GridElement } {
        return this._gridElements;
    }

    public getBetElements(): { [id: string]: BetElement } {
        if (this._gridBets != null) {
            return this._gridBets.getBetElements();
        }

        return null;
    }

    public getGridWinPoint(): BaseElement {
        if (this._gridWinPoint != null) {
            return <BaseElement>this._gridWinPoint.childs["Point"];
        }

        return null;
    }

    public placeSchemaBet(roulletBet: Bet): void {
        if (this._gridSchemaBets != null) {
            this._gridSchemaBets.placeBet(roulletBet);
        }

        if (this.childs["GridSchemaOverlay"] != null) {
            this.childs["GridSchemaOverlay"].visible = true;
        }
    }

    public updateSchemaPossibleWin(bets: Array<Bet>): void {
        if (this._gridSchemaPossibleWins != null) {
            this._gridSchemaPossibleWins.updatePossibleWins(bets);
        }
    }

    public resetSchemaBets(): void {
        if (this._gridSchemaBets != null) {
            this._gridSchemaBets.resetBets();
        }

        if (this._gridSchemaPossibleWins != null) {
            this._gridSchemaPossibleWins.resetPossibleWins();
        }

        if (this.childs["GridSchemaOverlay"] != null) {
            this.childs["GridSchemaOverlay"].visible = false;
        }
    }

    //******************************************************************************************************************
    //  LAYOUT
    //******************************************************************************************************************

    public layoutChanged(): void {
        if (this._gridBets != null) {
            this._gridBets.layoutChanged(
                this,
                RouletteGridConfiguration.gridWidth,
                RouletteGridConfiguration.gridHeight,
            );
        }
    }

    //******************************************************************************************************************
    //  HELPER FUNCTIONS
    //******************************************************************************************************************

    private setBitmapTextureByIndex(elem: SwitchElement, imageName: string, index: number): void {
        let node: Element = document.createElement("ImageTextureUpdate");
        let image = <ImageElement>elem.getChildElementByName("Image");
        node.setAttribute("Image", imageName + "_" + index);
        image.createByNode(node);
    }

    private setNumberBitmapText(elem: SwitchElement, text: string): void {
        let textElement = <ImageTextElement>elem.getChildElementByName("Text");
        textElement.bitmapText.text = text;
        textElement.realign();
    }

    private createGridElement(
        nodeName: string,
        style: string,
        portraitStyle: string,
        clone: boolean = false,
    ): GridElement {
        if (!StringUtils.isNullOrEmpty(style)) {
            let element: GridElement = new GridElement();
            let node: Element = document.createElement(nodeName);
            node.setAttribute("Style", style);
            node.setAttribute("Case", GridElement.CaseNormal);

            if (StyleManager.Styles[portraitStyle] != null) {
                node.setAttribute("PortraitStyle", portraitStyle);
            }

            if (clone) {
                node.setAttribute("Clone", "true");
            }

            element.createByNode(node);
            return element;
        }

        return null;
    }
}

/**
 * Can inherit attributes from BaseElement
 */
export const GridAttributes = {
    /*
     * Name of config for machine.
     * @property Configs
     * @type string
     * @default null
     */
    CONFIGS: "Configs",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Grid.ts
 **/